import type { QuizQuestion, QuizUploadedMedia } from "@core/features/quiz";
import type { ProcessedAnswer } from "../publicProfile.types";

const URL_PATTERN = /^https?:\/\//i;

/**
 * Process a quiz question to extract its answer data.
 */
export function processQuestion(question: QuizQuestion): ProcessedAnswer {
  const { id, prompt, internal_name, type, choices } = question;

  let textValue: string | undefined = question.user_answer_text || undefined;

  let selectedChoices: string[] | undefined;
  if (question.user_answer && question.user_answer.length > 0 && choices) {
    selectedChoices = question.user_answer
      .map((choiceId) => {
        const choice = choices.find((c) => c.id === choiceId);
        return choice?.display_text;
      })
      .filter((text): text is string => Boolean(text));
  }

  let uploadedMedia: QuizUploadedMedia[] | undefined =
    question.user_uploaded_media?.length
      ? question.user_uploaded_media
      : undefined;

  // Selected choices are the canonical source for choice-based answers; some
  // legacy rows also carry the same value as text and would render twice.
  if (selectedChoices && selectedChoices.length > 0) {
    textValue = undefined;
  }

  // Legacy image answers store the upload URL in answer_text with no
  // associated MediaUpload row. Promote it to the media path so it renders
  // as an image instead of a raw URL string.
  if (
    type === "image" &&
    !uploadedMedia &&
    textValue &&
    URL_PATTERN.test(textValue)
  ) {
    uploadedMedia = [
      {
        id: -id,
        file: textValue,
        name: "",
        size: 0,
        uploaded_at: "",
        file_type: "image",
        alt_text: prompt,
      },
    ];
    textValue = undefined;
  }

  const hasAnswer = Boolean(
    textValue ||
      (selectedChoices && selectedChoices.length > 0) ||
      (uploadedMedia && uploadedMedia.length > 0)
  );

  return {
    questionId: id,
    label: prompt,
    internalName: internal_name,
    type,
    textValue,
    selectedChoices,
    uploadedMedia,
    hasAnswer,
  };
}
